import React, { useEffect, useRef } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { getTruncatedChipLabel, indexViewStyles } from './indexView-helpers';

import {
    HexIndexNormal,
    HexIndexUnique,
    HexIndexPrimary,
    HexIndexFuzzy,
    HexIndexFullText,
    HexIndexText,
    HexIndexVector,
} from 'assets/indexes';
import { ErrorCard } from 'components';
import { CUSTOM_INDEX_TYPE, DictionaryIndex } from 'lib';
import { useIndexList } from 'pages/dictionaries/contexts/IndexListContext';
import { Chip, IconButton, Menu, MenuItem } from '@biq/apex-ui';
import MoreVertIcon from '@mui/icons-material/MoreVert';

interface DictionaryManagerIndexViewProps {
    index: DictionaryIndex;
    setIsChangesUnsaved?: React.Dispatch<React.SetStateAction<boolean>>;
}

const getIcon = (type: string): React.JSX.Element => {
    if (type?.toLowerCase() === 'normal') {
        return (
            <img
                alt='normal'
                src={HexIndexNormal}
                style={indexViewStyles.typeIcon}
            />
        );
    } else if (type?.toLowerCase() === 'primary') {
        return (
            <img
                alt='primary'
                src={HexIndexPrimary}
                style={indexViewStyles.typeIcon}
            />
        );
    } else if (type?.toLowerCase() === 'unique') {
        return (
            <img
                alt='unique'
                src={HexIndexUnique}
                style={indexViewStyles.typeIcon}
            />
        );
    } else if (type?.toLowerCase() === 'fuzzy') {
        return (
            <img
                alt='fuzzy'
                src={HexIndexFuzzy}
                style={indexViewStyles.typeIcon}
            />
        );
    } else if (type?.toLowerCase() === 'vector') {
        return (
            <img
                alt='vector'
                src={HexIndexVector}
                style={indexViewStyles.typeIcon}
            />
        );
    } else if (type?.toLowerCase() === 'fulltext') {
        return (
            <img
                alt='fulltext'
                src={HexIndexFullText}
                style={indexViewStyles.typeIcon}
            />
        );
    } else if (type?.toLowerCase() === 'keyword') {
        return (
            <img
                alt='keyword'
                src={HexIndexText}
                style={indexViewStyles.typeIcon}
            />
        );
    } else {
        return <ErrorCard title='no image found' />;
    }
};

export const IndexView = ({
    index,
    setIsChangesUnsaved,
}: DictionaryManagerIndexViewProps): React.JSX.Element => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { setSelectedIndex, deleteIndex } = useIndexList();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [textWidth, setTextWidth] = React.useState<number>(0);
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                if (containerRef.current) {
                    let width = containerRef.current.clientWidth - 110;
                    setTextWidth(width);
                }
            });

            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    const handleDeleteIndex = (): void => {
        setIsChangesUnsaved?.(true);
        deleteIndex(index);
    };

    const handleSelectIndexForEdit = (): void => {
        setSelectedIndex(index.id);
    };

    const handleClose = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const getTypeLabelByTypeKey = (type: string): string => {
        if (!type) return '';

        // this will be used to add the new custom types when they are ready
        if (type === 'KEYWORD') {
            return CUSTOM_INDEX_TYPE[type].toUpperCase();
        }

        return type;
    };

    return (
        <Grid sx={indexViewStyles.card} ref={containerRef}>
            <Box
                sx={indexViewStyles.containerBox}
                onClick={handleSelectIndexForEdit}
            >
                <Box sx={indexViewStyles.iconBox}>
                    <Box>{getIcon(index?.type)}</Box>
                </Box>
                <Box width={`${textWidth}px`}>
                    <Box sx={indexViewStyles.infoBox}>
                        <Box sx={indexViewStyles.contentBox}>
                            <Box>
                                <Box sx={indexViewStyles.nameBox}>
                                    <Typography sx={indexViewStyles.text}>
                                        Name:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...indexViewStyles.text,
                                            ...indexViewStyles.nametext,
                                        }}
                                    >
                                        {index?.name}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={indexViewStyles.nameBox}>
                                <Typography sx={indexViewStyles.typetext}>
                                    Type:
                                </Typography>
                                <Typography
                                    sx={{
                                        ...indexViewStyles.typetext,
                                        ...indexViewStyles.typeTextValue,
                                    }}
                                >
                                    {getTypeLabelByTypeKey(index?.type)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={indexViewStyles.attributeContainer}>
                        <Typography sx={indexViewStyles.attributeText}>
                            Attributes
                        </Typography>
                        <Box sx={indexViewStyles.attributeBox}>
                            {React.Children.toArray(
                                Array.isArray(index?.attributes) ? (
                                    index.attributes.map((i) => (
                                        <Chip
                                            key={i}
                                            label={getTruncatedChipLabel(i)}
                                            color='primary'
                                            size='medium'
                                            variant='outlined'
                                            readOnly
                                            sx={indexViewStyles.chipStyles}
                                        />
                                    ))
                                ) : index?.attributes ? (
                                    <Chip
                                        key={index?.attributes}
                                        label={getTruncatedChipLabel(
                                            index?.attributes
                                        )}
                                        size='medium'
                                        color='primary'
                                        variant='outlined'
                                        readOnly
                                        sx={indexViewStyles.chipStyles}
                                    />
                                ) : null
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box>
                    <>
                        <IconButton
                            aria-label='remove'
                            id='remove-button'
                            aria-controls={open ? 'remove-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-haspopup='true'
                            onClick={handleClick}
                            sx={indexViewStyles.tooltipIconButton}
                        >
                            <MoreVertIcon sx={indexViewStyles.tooltipIcon} />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            id='remove-menu'
                            open={open}
                            onClose={handleClose}
                            onClick={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'center',
                                horizontal: 'right',
                            }}
                            sx={indexViewStyles.menuStyles}
                        >
                            <MenuItem onClick={handleDeleteIndex}>
                                Remove
                            </MenuItem>
                        </Menu>
                    </>
                </Box>
            </Box>
        </Grid>
    );
};
